/**
 * Analytics Selectors
 * Memoized selectors for analytics state
 */

import { createSelector } from '@spcc/core'
import type { AnalyticsState } from './analytics.slice'
import type { ConsentState } from '../domain/types'

// ============================================
// Root State Type (to be augmented by app)
// ============================================

interface RootStateWithAnalytics {
  analytics: AnalyticsState
  consent: ConsentState
}

// ============================================
// Base Selectors
// ============================================

/** Select analytics slice */
export const selectAnalyticsSlice = (state: RootStateWithAnalytics) => state.analytics

/** Select consent slice */
export const selectConsentSlice = (state: RootStateWithAnalytics) => state.consent

// ============================================
// Analytics Selectors
// ============================================

/** Select session ID */
export const selectSessionId = createSelector(
  selectAnalyticsSlice,
  (analytics) => analytics.sessionId
)

/** Select whether analytics is initialized */
export const selectIsInitialized = createSelector(
  selectAnalyticsSlice,
  (analytics) => analytics.isInitialized
)

/** Select last page view path */
export const selectLastPageView = createSelector(
  selectAnalyticsSlice,
  (analytics) => analytics.lastPageView
)

/** Select page view start time */
export const selectPageViewStartTime = createSelector(
  selectAnalyticsSlice,
  (analytics) => analytics.pageViewStartTime
)

/** Select current user ID */
export const selectUserId = createSelector(
  selectAnalyticsSlice,
  (analytics) => analytics.userId
)

/** Select total event count */
export const selectEventCount = createSelector(
  selectAnalyticsSlice,
  (analytics) => analytics.eventCount
)

/** Select total error count */
export const selectErrorCount = createSelector(
  selectAnalyticsSlice,
  (analytics) => analytics.errorCount
)

/**
 * Calculate time spent on current page (in ms)
 */
export const selectCurrentPageDuration = createSelector(
  selectPageViewStartTime,
  (startTime) => Date.now() - startTime
)

// ============================================
// Consent Selectors
// ============================================

/** Select full consent state */
export const selectConsentState = createSelector(
  selectConsentSlice,
  (consent) => consent
)

/** Select whether analytics tracking is allowed */
export const selectAnalyticsConsent = createSelector(
  selectConsentSlice,
  (consent) => consent.analytics
)

/** Select whether marketing tracking is allowed */
export const selectMarketingConsent = createSelector(
  selectConsentSlice,
  (consent) => consent.marketing
)

/** Select whether preferences tracking is allowed */
export const selectPreferencesConsent = createSelector(
  selectConsentSlice,
  (consent) => consent.preferences
)

/** Select whether user has made consent decision */
export const selectHasConsentDecision = createSelector(
  selectConsentSlice,
  (consent) => consent.hasDecided
)

/** Select whether consent banner should be shown */
export const selectShowConsentBanner = createSelector(
  selectConsentSlice,
  (consent) => consent.showBanner
)

/** Select whether consent settings modal is open */
export const selectConsentSettingsOpen = createSelector(
  selectConsentSlice,
  (consent) => consent.settingsOpen
)

// ============================================
// Combined Selectors
// ============================================

/**
 * Select whether tracking is currently enabled
 * (initialized + consent granted)
 */
export const selectIsTrackingEnabled = createSelector(
  selectIsInitialized,
  selectAnalyticsConsent,
  (initialized, consent) => initialized && consent
)

/**
 * Select analytics context for events
 * Contains all data needed to build an event
 */
export const selectAnalyticsContext = createSelector(
  selectSessionId,
  selectUserId,
  selectLastPageView,
  selectPageViewStartTime,
  (sessionId, userId, lastPageView, pageViewStartTime) => ({
    sessionId,
    userId,
    lastPageView,
    pageViewStartTime,
    currentPageDuration: Date.now() - pageViewStartTime,
  })
)

/**
 * Select consent summary for UI
 */
export const selectConsentSummary = createSelector(
  selectConsentSlice,
  (consent) => ({
    hasDecided: consent.hasDecided,
    showBanner: consent.showBanner,
    settingsOpen: consent.settingsOpen,
    categories: {
      necessary: true,
      analytics: consent.analytics,
      marketing: consent.marketing,
      preferences: consent.preferences,
    },
  })
)
